// A full-screen "Updating…" overlay shown in the moments before we reload the
// page onto a new build.
//
// Why plain DOM instead of a React component: the reload can be triggered from
// version-check.ts or the service-worker controllerchange handler, both of
// which run outside React and can fire before the app has even mounted. A
// React overlay would need state plumbed through a tree that's about to be
// destroyed anyway. Injecting a node is immediate, dependency-free, and
// guaranteed to paint before location.reload().
//
// Without this the post-deploy experience was: blank screen, app appears,
// then it silently reloads to a blank screen and appears again — which reads
// as "the app is broken / taking forever" rather than "we're updating".

const OVERLAY_ID = "cornholio-updating";

export function showUpdatingOverlay(
  message = "Updating to the latest version…",
): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(OVERLAY_ID)) return;

  const el = document.createElement("div");
  el.id = OVERLAY_ID;
  el.setAttribute("role", "status");
  el.setAttribute("aria-live", "polite");
  el.style.cssText = [
    "position:fixed",
    "inset:0",
    "z-index:2147483647",
    "display:flex",
    "flex-direction:column",
    "align-items:center",
    "justify-content:center",
    "gap:14px",
    "background:#0e1f4d",
    "color:#fff",
    "font-family:ui-sans-serif,system-ui,sans-serif",
  ].join(";");

  const ring = document.createElement("div");
  ring.style.cssText = [
    "width:34px",
    "height:34px",
    "border:3px solid rgba(255,255,255,0.25)",
    "border-top-color:#fff",
    "border-radius:50%",
    "animation:cornholioUpdSpin 0.8s linear infinite",
  ].join(";");

  const msg = document.createElement("div");
  msg.textContent = message;
  msg.style.cssText =
    "font-size:13px;letter-spacing:0.08em;text-transform:uppercase;opacity:0.9;text-align:center;padding:0 24px";

  const sub = document.createElement("div");
  sub.textContent = "This only takes a moment.";
  sub.style.cssText = "font-size:12px;opacity:0.6";

  // Keyframes need a stylesheet; inline styles can't declare them.
  const style = document.createElement("style");
  style.textContent =
    "@keyframes cornholioUpdSpin{to{transform:rotate(360deg)}}";

  el.append(style, ring, msg, sub);
  document.body.appendChild(el);
}
